import React, { useEffect, useState, useRef } from 'react';
import _ from 'lodash';
import { Transition } from '@headlessui/react';
import { useClickAway } from 'react-use';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AnimatePresence } from 'framer-motion';
import { faArrowLeft, faArrowRight, faSearch } from '@fortawesome/pro-duotone-svg-icons';
import Button from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import Input from '../../shared-components/form/Input';
import PhoneInput from '../../shared-components/form/PhoneInput';
import Select from '../../shared-components/form/Select';
import Checkbox from '../../shared-components/form/Checkbox';
import { userAtom, userPrivilagesAtom, notificationsAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1, H2, H4 } from '../../shared-components/typography/Title';
import rayo from '../../../assets/images/characters/rayo.png';
import {
  prMunicipalities,
  ageGroups,
  salaryGroups,
  averageMonthlyPaymentGroups,
  creditScoreGroups,
  roofTypes,
  mapContainerStyle
} from '../../../utils';
import api from '../../../api/api';
import './shared-components/leadgen.css';
import envConfig from '../../../envConfig';

function LeadGenLanding() {
  const suggestionsRef = useRef();
  const { orgReferralCode } = useParams();
  const user = useRecoilValue(userAtom);
  const [isChecked, setIsChecked] = useState(false);
  const { t, i18n } = useTranslation();
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [currentStep, setCurrentStep] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [organization, setOrganization] = useState(null);
  const [loadingOrganization, setLoadingOrganization] = useState(false);
  const textValidation = Yup.string().required(t('required_field'));
  const emailValidation = Yup.string()
                             .email(t('valid_email_validation'))
                             .required(t('required_field'));
  const phoneValidation = Yup.string()
                             .required(t('required_field'));
  const [state, setState] = useState({
    draggable: true,
    suggestions: [],
  });
  const defaultZoom = 11;

  function handleCheckboxChange() {
    setIsChecked(!isChecked);
  }

  const formDefaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '+1',
    municipality: prMunicipalities[0],
    ageGroup: ageGroups[0].id,
    salaryGroup: salaryGroups[0].id,
    isPropertyOwner: false,
    averageMonthlyPaymentGroup: averageMonthlyPaymentGroups[0].id,
    creditScoreGroup: creditScoreGroups[0].id,
    roofType: roofTypes[2].id,
    latitude: 18.3977264,
    longitude: -66.1040551
  };

  const authenticatedSteps = [
    {
      key: 'municipality',
      title: t('municipality_prompt'),
      step: 2,
    },
    {
      key: 'age',
      title: t('age_prompt'),
      step: 3,
    },
    {
      key: 'salary',
      title: t('salary_range'),
      step: 4,
    },
    {
      key: 'propertyowner',
      title: t('are_property_owner_prompt'),
      step: 5,
    },
    {
      key: 'monthlypayment',
      title: t('average_monthly_payment'),
      step: 6,
    },
    {
      key: 'creditscore',
      title: t('credit_score'),
      step: 7,
    },
    {
        key: 'review_information',
        title: t('review_before_submitting'),
        step: 8,
      },
    // {
    //   key: 'rooftype',
    //   title: t('roof_type'),
    //   step: 8,
    // },
    // {
    //   key: 'location',
    //   title: t('location'),
    //   step: 9,
    // }
  ];

  const nonAuthenticatedSteps = [
    {
      key: 'userinfo',
      title: t('contact_information'),
      step: 1,
    },
    ...authenticatedSteps
  ];

  const formik = useFormik({
    initialValues: formDefaultValues,
    validationSchema: Yup.object().shape({
      firstName: textValidation,
      lastName: textValidation,
      email: emailValidation,
      phone: phoneValidation,
      municipality: textValidation
    }),
    onSubmit: (values, { setSubmitting }) => {
      setLoading(true);
      api.post('leads/new', {
        ...values,
        OrgReferralCode: orgReferralCode
      })
        .then((response) => {
          setLoading(false);
          if (response?.data?.success) {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: response.data.message,
                error: false,
              },
            ]);
            formik.setValues(formDefaultValues);
            // setCurrentStep(user ? 2 : 1);
            setCurrentStep(1);
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: response?.data?.error,
                error: true,
              },
            ]);
          }
        })
        .catch((error) => {
          setLoading(false);
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            },
          ]);
        });
    },
  });

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: envConfig.GoogleApiKey,
    // language: `${i18n.language}-US`,
    language: 'en-US',
    libraries: ['places']
  });

  const apiHasLoaded = (map) => {
    const placesService = new window.google.maps.places.PlacesService(map);
    const autocompleteService = new window.google.maps.places.AutocompleteService();
    const bounds = new window.google.maps.LatLngBounds({
      lat: formik.values.latitude,
      lng: formik.values.longitude,
    });
    map.fitBounds(bounds);
    map.setZoom(defaultZoom);
    setState({
      ...state,
      mapApiLoaded: true,
      mapInstance: map,
      placesService,
      autocompleteService,
    });
    setAddress('');
  };

  function isStepValid() {
    if (currentStep === 1) {
      const check1 = textValidation.isValidSync(formik.values.firstName);
      const check2 = textValidation.isValidSync(formik.values.lastName);
      const check3 = emailValidation.isValidSync(formik.values.email);
      const check4 = phoneValidation.isValidSync(formik.values.phone);
      return check1 && check2 && check3 && check4;
    }
    if (currentStep === 2) {
      return textValidation.isValidSync(formik.values.municipality);
    }
    if (currentStep === 3) {
      return formik.values.ageGroup > -1;
    }
    if (currentStep === 4) {
      return formik.values.salaryGroup > -1;
    }
    if (currentStep === 6) {
      return formik.values.averageMonthlyPaymentGroup > -1;
    }
    if (currentStep === 7) {
      return formik.values.creditScoreGroup > -1;
    }
    return true;
  }

  function canGoBack() {
    let isFirst = false;
    if (user) {
      // isFirst = authenticatedSteps[0].step === currentStep;
      isFirst = nonAuthenticatedSteps[0].step === currentStep;
    } else {
      isFirst = nonAuthenticatedSteps[0].step === currentStep;
    }
    if (isFirst) {
      return false;
    }
    return true;
  }

  function canGoForward() {
    let isLast = false;
    if (user) {
      // isLast = authenticatedSteps[authenticatedSteps.length - 1].step === currentStep;
      isLast = nonAuthenticatedSteps[nonAuthenticatedSteps.length - 1].step === currentStep;
    } else {
      isLast = nonAuthenticatedSteps[nonAuthenticatedSteps.length - 1].step === currentStep;
    }
    if (isLast) {
      return false;
    }
    return true;
  }

  function getNextButtonText() {
    if (currentStep === 7) {
        return t('review');
      }
    if (!canGoForward()) {
      return t('submit');
    }
    return t('next');
  }

  function returnAgeText(age) {    
    if (age === 'LessThanThirty') return '< 30'
    if (age === 'MoreThanSixty') return '> 60'
    return age;
  }

  function returnSalaryText(salary) {    
    if (salary === 'MoreThanOneHundredFiftyK') return '> $150,000'
    return salary;
  }

  function returnCreditScoreText(score) {    
    if (score === 'LessThanSixHoundredSeventy') return '< 670'
    if (score === 'idk') return t('idk')
    return score;
  }

  function returnMonthlyBillText(bill) {    
    if (bill === 'LessThanHundred') return '< $100'
    if (bill === 'MoreThanFourHundred') return t('> $400')
    return bill;
  }

  const fetchPredictions = () => {
    if (address.length && state.mapApiLoaded) {
      state.autocompleteService.getPlacePredictions(
        {
          input: address,
          componentRestrictions: { country: 'pr' },
        },
        (predictions, status) => {
          if (status !== 'OK') {
            return;
          }

          setState({
            ...state,
            suggestions: predictions,
          });
        }
      );
    } else {
      setState({
        ...state,
        suggestions: [],
      });
    }
  };

  const getDetails = (placeId) => {
    state.placesService.getDetails(
      {
        placeId,
        fields: [
          'name',
          'address_components',
          'formatted_address',
          'geometry',
          'place_id',
        ],
      },
      (result, status) => {
        if (status === 'OK') {
          state.mapInstance.fitBounds(result.geometry.viewport);
          _.each(result.address_components, (component) => {
            if (component.types[0] === 'locality') {
              const muns = _.filter(prMunicipalities, (mun) => mun === component.long_name);
              if (muns?.length > 0) {
                return formik.setFieldValue('municipality', component.long_name);
              }
            }
            return null;
          });
          formik.setFieldValue('latitude', result.geometry.location.lat());
          formik.setFieldValue('longitude', result.geometry.location.lng());
          setState({
            ...state,
            draggable: true,
            suggestions: [],
          });
          setAddress('');
        }
      }
    );
  };

  useEffect(() => {
    fetchPredictions();
  }, [address]);

  useEffect(() => {
    formik.setValues(formDefaultValues);
    if (user) {
      // setCurrentStep(2);
      formik.setFieldValue('firstName', user?.firstName);
      formik.setFieldValue('lastName', user?.lastName);
      formik.setFieldValue('email', user?.email);
      formik.setFieldValue('phone', user?.phone);
      setCurrentStep(1);
    } else {
      setCurrentStep(1);
    }
  }, [user]);

  useEffect(() => {
    if (formik?.values?.municipality && state.mapApiLoaded) {
      state.autocompleteService.getPlacePredictions(
        {
          input: formik?.values?.municipality,
          componentRestrictions: { country: 'pr' },
        },
        (predictions, predictionStatus) => {
          if (predictionStatus !== 'OK') {
            return;
          }
          if (predictions?.length > 0) {
            const placeId = predictions[0].place_id;
            state.placesService.getDetails(
              {
                placeId,
                fields: [
                  'name',
                  'address_components',
                  'formatted_address',
                  'geometry',
                  'place_id',
                ],
              },
              (detailsResult, detailsStatus) => {
                if (detailsStatus === 'OK') {
                  state.mapInstance.fitBounds(detailsResult.geometry.viewport);
                  formik.setFieldValue('latitude', detailsResult.geometry.location.lat());
                  formik.setFieldValue('longitude', detailsResult.geometry.location.lng());
                }
              }
            );
          }
        }
      );
    }
  }, [formik?.values?.municipality]);

  useEffect(() => {
    setAddress('');
    formik.setValues(formDefaultValues);
    setTimeout(() => {
      if (user) {
        // setCurrentStep(2);
        formik.setFieldValue('firstName', user?.firstName);
        formik.setFieldValue('lastName', user?.lastName);
        formik.setFieldValue('email', user?.email);
        formik.setFieldValue('phone', user?.phone);
        setCurrentStep(1);
      } else {
        setCurrentStep(1);
      }
      setShowForm(true);
    }, 750);
  }, []);

  useEffect(() => {
    if (orgReferralCode) {
      setLoadingOrganization(true);
      api.get(`organizations?rc=${orgReferralCode}`)
        .then((response) => {
          setLoadingOrganization(false);
          if (response?.data?.success) {
            setOrganization(response.data.data);
          } else {
            setOrganization(null);
          }
        })
        .catch((error) => {
          setLoadingOrganization(false);
          setOrganization(null);
        });
    }
  }, [orgReferralCode]);

  useClickAway(suggestionsRef, () => {
    setState({
      ...state,
      suggestions: [],
    });
  });

  return (
    <MainPage>
      <div className="space-y-8">
        <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-12">
          <div className="col-span-1 lg:col-span-7">
            {organization && (
              <>
                {!organization?.logo && (
                  <H1 className="text-center text-xl" text={organization?.nameEn} />
                )}
                {organization?.logo && (
                  <img src={organization?.logo} alt={organization?.nameEn} className="w-auto h-24" />
                )}
              </>
            )}
            <H1 className="text-center text-xl" text={t('prequalification_prompt')} />
            <div className="flex flex-row items-center gap-4">  
              {/* {organization?.logo && (
                <img src={organization?.logo} alt={organization?.nameEn} className="w-1/4 h-auto" />
              )} */}
              <div>
                <p className="mt-4 font-semibold text-mdtext-primaryColor">{t('prequalification_requiredInfo')}</p>
                <ul className="list-disc pl-5 text-md md:text-lg mt-8">
                  <li>{t('prequal.contactInformation')}</li>
                  <li>{t('prequal.locationMunicipality')}</li>
                  <li>{t('prequal.ageGroup')}</li>
                  <li>{t('prequal.incomeBracket')}</li>
                  <li>{t('prequal.propertyOwnership')}</li>
                  <li>{t('prequal.monthlyBill')}</li>
                  <li>{t('prequal.creditScore')}</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-span-1 lg:col-span-5 bg-white p-6">
            <AnimatePresence>
              <Form className={`${showForm ? '' : 'hidden'} leadForm grid gap-2 grid-cols-1 md:grid-cols-2`}>
                <H4
                  className="col-span-2"
                  // text={user ? authenticatedSteps[currentStep - 1].title : nonAuthenticatedSteps[currentStep - 1].title}
                  text={nonAuthenticatedSteps[currentStep - 1].title}
                />
                {currentStep === 1 && (
                  <>
                  <Input
                    className="col-span-2"
                    label={t('name')}
                    required
                    type="text"
                    name="firstName"
                    id="firstName"
                    disabled={loading}
                    autoComplete="given-name"
                    autoCapitalize="true"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    error={
                    formik.errors.firstName && formik.touched.firstName && formik.errors.firstName
                    }
                  />
                    <Input
                      className="col-span-2"
                      label={t('last_name')}
                      required
                      type="text"
                      name="lastName"
                      id="lastName"
                      disabled={loading}
                      autoComplete="family-name"
                      autoCapitalize="true"
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      error={
                        formik.errors.lastName && formik.touched.lastName && formik.errors.lastName
                      }
                    />
                    <Input
                      className="col-span-2"
                      label={t('email')}
                      required
                      type="email"
                      name="email"
                      id="email"
                      disabled={loading}
                      autoComplete="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={
                        formik.errors.email && formik.touched.email && formik.errors.email
                      }
                    />
                    <PhoneInput
                      className="col-span-2"
                      label={t('phone')}
                      required
                      type="phone"
                      name="phone"
                      id="phone"
                      disabled={loading}
                      autoComplete="phone"
                      value={formik.values.phone}
                      onChange={(number) => {
                        formik.setFieldValue('phone', number);
                      }}
                      error={
                        formik.errors.phone && formik.touched.phone && formik.errors.phone
                      }
                    />                   
                  </>
                )}
                {currentStep === 2 && (
                  <>
                    <Select
                      className="col-span-2"
                      id="municipality"
                      name="municipality"
                      disabled={loading}
                      value={formik.values.municipality}
                      onChange={(obj) => {
                        formik.setFieldValue('municipality', obj.target.value);
                      }}
                      options={_.map(prMunicipalities, (m) => ({
                        key: m,
                        value: m,
                        text: m,
                        disabled: false,
                      }))}
                    />
                    <div className="relative text-center mt-4 col-span-2">
                      <span>{t('move_marker_on_map_or_search')}</span>
                      <div className="flex">
                        <Input
                          icon={faSearch}
                          value={address}
                          onChange={(obj) => setAddress(obj.target.value)}
                        />
                      </div>
                      <Transition
                        appear={state.suggestions.length > 0}
                        show={state.suggestions.length > 0}
                        enter="transform transition duration-500"
                        enterFrom="opacity-0 -translate-y-50"
                        enterTo="opacity-100 -translate-y-0"
                        leave="transform transition duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        className="absolute z-10 mt-2 w-full divide-y divide-gray-100 shadow-xl bg-white px-4"
                      >
                        {_.map(state.suggestions, (suggestion) => (
                          <button
                            key={suggestion.place_id}
                            type="button"
                            onClick={() => getDetails(suggestion.place_id)}
                            className="text-gray-400 hover:text-gray-500 text-xs text-left block w-full py-4 px-4 truncate ring-0 focus:ring-0 outline-none focus:outline-none transition duration-200 ease-linear cursor-pointer"
                          >
                            {suggestion.description}
                          </button>
                        ))}
                      </Transition>
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        zoomControl
                        draggable
                        mapTypeId="satellite"
                        center={{
                          lat: formik.values.latitude,
                          lng: formik.values.longitude,
                        }}
                        zoom={defaultZoom}
                        onLoad={apiHasLoaded}
                      >
                        <Marker
                          draggable
                          position={{ lat: formik.values.latitude, lng: formik.values.longitude }}
                          onDragEnd={(coords) => {
                            const lat = coords.latLng.lat();
                            const lon = coords.latLng.lng();
                            formik.setFieldValue('latitude', lat);
                            formik.setFieldValue('longitude', lon);
                          }}
                        />
                      </GoogleMap>
                    </div>
                  </>
                )}
                {currentStep === 3 && (
                  <Select
                    className="col-span-2"
                    id="age"
                    name="age"
                    disabled={loading}
                    value={formik.values.ageGroup}
                    onChange={(obj) => {
                      formik.setFieldValue('ageGroup', parseInt(obj.target.value, 10));
                    }}
                    options={_.map(ageGroups, (m) => ({
                      key: m.id,
                      value: m.id,
                      text: t(m.name),
                      disabled: false,
                    }))}
                  />
                )}
                {currentStep === 4 && (
                  <Select
                    className="col-span-2"
                    id="salary"
                    name="salary"
                    disabled={loading}
                    value={formik.values.salaryGroup}
                    onChange={(obj) => {
                      formik.setFieldValue('salaryGroup', parseInt(obj.target.value, 10));
                    }}
                    options={_.map(salaryGroups, (m) => ({
                      key: m.id,
                      value: m.id,
                      text: t(m.name),
                      disabled: false,
                    }))}
                  />
                )}
                {currentStep === 5 && (
                  <>
                    <Checkbox
                      label={t('yes')}
                      parentClassName="col-span-1"
                      disabled={loading}
                      id="isPropertyOwner"
                      name="isPropertyOwner"
                      value={formik.values.isPropertyOwner}
                      onChange={(obj) => {
                        formik.setFieldValue('isPropertyOwner', true);
                      }}
                    />
                    <Checkbox
                      label={t('no')}
                      parentClassName="col-span-1"
                      disabled={loading}
                      id="isPropertyOwnerNo"
                      name="isPropertyOwnerNo"
                      value={!formik.values.isPropertyOwner}
                      onChange={(obj) => {
                        formik.setFieldValue('isPropertyOwner', false);
                      }}
                    />
                  </>
                )}
                {currentStep === 6 && (
                  <Select
                    className="col-span-2"
                    id="payment"
                    name="payment"
                    disabled={loading}
                    value={formik.values.averageMonthlyPaymentGroup}
                    onChange={(obj) => {
                      formik.setFieldValue('averageMonthlyPaymentGroup', parseInt(obj.target.value, 10));
                    }}
                    options={_.map(averageMonthlyPaymentGroups, (m) => ({
                      key: m.id,
                      value: m.id,
                      text: t(m.name),
                      disabled: false,
                    }))}
                  />
                )}
                {currentStep === 7 && (
                  <Select
                    className="col-span-2"
                    id="credit"
                    name="credit"
                    disabled={loading}
                    value={formik.values.creditScoreGroup}
                    onChange={(obj) => {
                      formik.setFieldValue('creditScoreGroup', parseInt(obj.target.value, 10));
                    }}
                    options={_.map(creditScoreGroups, (m) => ({
                      key: m.id,
                      value: m.id,
                      text: t(m.name),
                      disabled: false,
                    }))}
                  />
                )}
                 {currentStep === 8 && (
                   <div>
                    <div>                  
                        <p className="font-semibold text-blue-500">{t('contact_information')}</p>
                        <p>{`${formik.values.firstName} ${formik.values.lastName}`}</p>
                        <p>{`${formik.values.email}`} </p>
                        <p>{`${formik.values.phone}`} </p>
                    </div>
                    <div>                  
                        <p className="font-semibold text-blue-500">{t('prequalifying_answers')}</p>
                        <p>{`${t('municipality')}: ${formik.values.municipality}`}</p>
                        <p>{`${t('age')}: ${returnAgeText(ageGroups[formik.values.ageGroup].name)}`}</p>
                        <p>{`${t('salary_range')}: ${returnSalaryText(salaryGroups[formik.values.salaryGroup].name)}`}</p>
                        <p>{`${t('property_owner')}: ${formik.values.isPropertyOwner ? 'Yes' : 'No'}`}</p>
                        <p>{`${t('average_monthly_payment')}: ${returnMonthlyBillText(averageMonthlyPaymentGroups[formik.values.averageMonthlyPaymentGroup].name)}`}</p>
                        <p>{`${t('credit_score')}: ${returnCreditScoreText(creditScoreGroups[formik.values.creditScoreGroup].name)}`}</p>
                    </div>
                   </div>
                )}
                {currentStep === 8 && 
                    <div className="flex col-span-2 text-xs">
                        <div>
                            <input
                              type="checkbox"
                              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                              checked={isChecked}
                              onChange={handleCheckboxChange}
                            />
                        </div>
                        <div>
                        {t('submit_disclaimer')}
                        </div>
                    </div>}
                <div className="flex col-span-2">
                  <Button
                    className={`${canGoBack() ? '' : 'hidden'} bg-primaryColor mb-6 px-6`}
                    textColor="text-primaryTextColor"
                    loaderColor="bg-primaryTextColor"
                    borderColor="border-primaryBorderColor"
                    disabled={loading}
                    text={t('back')}
                    icon={faArrowLeft}
                    onClick={() => {
                      if (canGoBack()) {
                        setCurrentStep(currentStep - 1);
                      }
                    }}
                  />
                  {canGoForward() && (
                    <Button
                      className="ml-auto bg-primaryColor mb-6 px-6"
                      textColor="text-primaryTextColor"
                      loaderColor="bg-primaryTextColor"
                      borderColor="border-primaryBorderColor"
                      loading={loading}
                      disabled={!isStepValid()}
                      text={getNextButtonText()}
                      icon={faArrowRight}
                      iconRight
                      onClick={() => setCurrentStep(currentStep + 1)}
                    />
                  )}
                  {!canGoForward() && (
                    <Button
                      className="ml-auto bg-primaryColor mb-6 px-6"
                      textColor="text-primaryTextColor"
                      loaderColor="bg-primaryTextColor"
                      borderColor="border-primaryBorderColor"
                      loading={loading}
                      disabled={!isStepValid() || !isChecked}
                      text={getNextButtonText()}
                      onClick={formik.submitForm}
                    />
                  )}
                </div>
              </Form>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </MainPage>
  );
}

export default LeadGenLanding;
